import React, { useCallback, useRef, useState } from "react";
import { withJsonFormsControlProps } from "@jsonforms/react";
import {
    ControlProps,
    rankWith,
    schemaMatches,
    RankedTester,
} from "@jsonforms/core";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Slide from "@mui/material/Slide";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { TransitionProps } from "@mui/material/transitions";
import { useTranslation } from "react-i18next";
import { apiUploadFiles, apiCall, apiCallBlob } from "../../services/apiClient";

interface AttachmentItem {
    attachmentId: string;
    fileName: string;
    fileType: string;
}

export const isPreviewable = (fileType: string) =>
    fileType.startsWith("image/");

export const getFileIcon = (fileType: string) => {
    if (fileType.startsWith("image/")) return <ImageIcon />;
    if (fileType === "application/pdf") return <PictureAsPdfIcon />;
    return <InsertDriveFileIcon />;
};

export interface FileValidationResult {
    valid: boolean;
    errorKey?: string;
    errorParams?: Record<string, unknown>;
}

export const validateFiles = (
    files: File[],
    currentCount: number,
    maxItems: number,
    acceptedFileTypes: string[],
    maxFileSize: number
): FileValidationResult => {
    if (files.length > maxItems - currentCount) {
        return {
            valid: false,
            errorKey: "forms.tooManyFiles",
            errorParams: { maxFiles: maxItems },
        };
    }

    for (const file of files) {
        if (!acceptedFileTypes.includes(file.type)) {
            return { valid: false, errorKey: "forms.invalidFileType" };
        }
        if (file.size > maxFileSize) {
            return {
                valid: false,
                errorKey: "forms.fileTooLarge",
                errorParams: {
                    maxSize: Math.round(maxFileSize / (1024 * 1024)),
                },
            };
        }
    }

    return { valid: true };
};

const SlideTransition = React.forwardRef(function Transition(
    props: TransitionProps & { children: React.ReactElement },
    ref: React.Ref<unknown>
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const AttachmentRendererComponent = ({
    data,
    handleChange,
    path,
    schema,
    uischema,
    enabled,
    config,
}: ControlProps) => {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [previewItem, setPreviewItem] = useState<AttachmentItem | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    const items: AttachmentItem[] = Array.isArray(data) ? data : [];
    const maxItems = schema.maxItems ?? 5;
    const options = (uischema.options ?? {}) as Record<string, unknown>;
    const acceptedFileTypes = (options.acceptedFileTypes as string[]) ?? [
        "image/png",
        "image/jpeg",
        "application/pdf",
    ];
    const maxFileSize = (options.maxFileSize as number) ?? 10 * 1024 * 1024;
    const maxFileSizeMB = Math.round(maxFileSize / (1024 * 1024));
    const sessionId = (config as Record<string, unknown>)
        ?.attachmentSessionId as string | undefined;

    const handleFileSelect = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = e.target.files;
            if (!files || files.length === 0 || !sessionId) return;

            setError(null);

            const selectedFiles = Array.from(files);
            const validation = validateFiles(
                selectedFiles,
                items.length,
                maxItems,
                acceptedFileTypes,
                maxFileSize
            );
            if (!validation.valid) {
                setError(
                    t(
                        validation.errorKey!,
                        validation.errorParams as Record<string, unknown>
                    )
                );
                if (fileInputRef.current) fileInputRef.current.value = "";
                return;
            }

            setUploading(true);
            try {
                const formData = new FormData();
                for (const file of selectedFiles) {
                    formData.append("files", file);
                }

                const response = await apiUploadFiles<AttachmentItem[]>(
                    `FormAnswerAttachments/upload?sessionId=${encodeURIComponent(sessionId)}`,
                    "POST",
                    formData
                );

                const newItems = [...items, ...response.data];
                handleChange(path, newItems);
            } catch {
                setError(t("forms.uploadFailed"));
            } finally {
                setUploading(false);
                if (fileInputRef.current) fileInputRef.current.value = "";
            }
        },
        [
            sessionId,
            items,
            maxItems,
            acceptedFileTypes,
            maxFileSize,
            maxFileSizeMB,
            handleChange,
            path,
            t,
        ]
    );

    const handleRemove = useCallback(
        async (attachmentId: string) => {
            try {
                await apiCall(
                    `FormAnswerAttachments/${encodeURIComponent(attachmentId)}`,
                    "DELETE"
                );
            } catch {
                // Best-effort delete - remove from UI even if API fails
            }
            const newItems = items.filter(
                (item) => item.attachmentId !== attachmentId
            );
            handleChange(path, newItems.length > 0 ? newItems : undefined);
        },
        [items, handleChange, path]
    );

    const handlePreview = useCallback(async (item: AttachmentItem) => {
        if (!isPreviewable(item.fileType)) return;
        setPreviewItem(item);
        setPreviewLoading(true);
        try {
            const response = await apiCallBlob(
                `FormAnswerAttachments/${encodeURIComponent(item.attachmentId)}/download`,
                "GET"
            );
            const url = URL.createObjectURL(response.data);
            setPreviewUrl(url);
        } catch {
            setPreviewItem(null);
        } finally {
            setPreviewLoading(false);
        }
    }, []);

    const handleClosePreview = useCallback(() => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewItem(null);
        setPreviewUrl(null);
    }, [previewUrl]);

    const isReadonly =
        !enabled || (config as Record<string, unknown>)?.readonly === true;

    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                {schema.title}
            </Typography>
            {schema.description && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                >
                    {schema.description}
                </Typography>
            )}
            {items.length > 0 && (
                <List dense>
                    {items.map((item) => (
                        <ListItem
                            key={item.attachmentId}
                            data-testid={`attachment-item-${item.attachmentId}`}
                            disablePadding
                            secondaryAction={
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    {isPreviewable(item.fileType) && (
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() =>
                                                void handlePreview(item)
                                            }
                                        >
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                    {!isReadonly && (
                                        <IconButton
                                            edge="end"
                                            size="small"
                                            color="error"
                                            onClick={() =>
                                                void handleRemove(
                                                    item.attachmentId
                                                )
                                            }
                                            data-testid={`attachment-remove-${item.attachmentId}`}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>
                            }
                        >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                                {getFileIcon(item.fileType)}
                            </ListItemIcon>
                            <ListItemText
                                primary={item.fileName}
                                primaryTypographyProps={{
                                    variant: "body2",
                                    noWrap: true,
                                }}
                            />
                        </ListItem>
                    ))}
                </List>
            )}
            {!isReadonly && items.length < maxItems && (
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept={acceptedFileTypes.join(",")}
                        style={{ display: "none" }}
                        onChange={(e) => void handleFileSelect(e)}
                        data-testid="attachment-file-input"
                    />
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={
                            uploading ? (
                                <CircularProgress size={16} />
                            ) : (
                                <AttachFileIcon />
                            )
                        }
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        data-testid="attachment-add-files"
                    >
                        {uploading
                            ? t("forms.uploadingFile")
                            : t("forms.addFiles")}
                    </Button>
                </>
            )}
            {error && (
                <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5 }}
                    data-testid="attachment-error"
                >
                    {error}
                </Typography>
            )}
            <Dialog
                open={previewItem !== null}
                onClose={handleClosePreview}
                fullScreen
                TransitionComponent={SlideTransition}
            >
                <AppBar sx={{ position: "relative" }}>
                    <Toolbar>
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={handleClosePreview}
                        >
                            <CloseIcon />
                        </IconButton>
                        <Typography sx={{ ml: 2, flex: 1 }} variant="h6" noWrap>
                            {previewItem?.fileName}
                        </Typography>
                    </Toolbar>
                </AppBar>
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "auto",
                        bgcolor: "background.default",
                    }}
                >
                    {previewLoading && <CircularProgress />}
                    {previewUrl && previewItem && (
                        <img
                            src={previewUrl}
                            alt={previewItem.fileName}
                            style={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain",
                            }}
                        />
                    )}
                </Box>
            </Dialog>
        </Box>
    );
};

export const attachmentRendererTester: RankedTester = rankWith(
    10,
    schemaMatches((schema) => schema.format === "attachment")
);

export const AttachmentRenderer = withJsonFormsControlProps(
    AttachmentRendererComponent
);
